import { type BranchListItem, type BranchListQuery } from '@utazzo/shared';
import { UzStatusBadge } from '@utazzo/ui';
import React, { useMemo } from 'react';

import { listBranches } from '../branches.api';
import { BranchFormDrawer } from '../components/BranchFormDrawer';
import {
  useBulkDeleteBranches,
  useBulkSetBranchesStatus,
  useBranchesList,
  useCountryOptions,
  useDeleteBranch,
} from '../use-branches';

import { type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { ListScreen, type ListScreenColumns } from '@/components/list-screen';
import { useCanExportFinanceData, useCurrentUser } from '@/features/auth/use-auth';

const STATUS_OPTIONS = [
  { value: '', label: 'Active + inactive' },
  { value: 'active', label: 'Active only' },
  { value: 'inactive', label: 'Inactive only' },
] as const;

/** Server search only fires from this many characters. */
const MIN_SEARCH_LENGTH = 3;

export const BranchesListPage = (): React.JSX.Element => {
  const me = useCurrentUser();
  const canExportFinance = useCanExportFinanceData();
  const canDelete = me?.role === 'ADMIN';
  const deleteBranch = useDeleteBranch();
  const bulkDeleteMut = useBulkDeleteBranches();
  const bulkSetStatusMut = useBulkSetBranchesStatus();
  const countriesQuery = useCountryOptions();

  const countryOptions = useMemo(
    () => [
      { value: '', label: 'All countries' },
      ...(countriesQuery.data ?? []).map((c) => ({
        value: c.id,
        label: `${c.name} (${c.isoCode.toUpperCase()})`,
      })),
    ],
    [countriesQuery.data],
  );

  const filters: ReadonlyArray<DataGridFilter> = useMemo(
    () => [
      {
        id: 'country',
        label: 'Country',
        options: countryOptions,
        widthClass: 'w-56',
      },
      {
        id: 'status',
        label: 'Status',
        options: STATUS_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
        widthClass: 'w-48',
      },
    ],
    [countryOptions],
  );

  const columns: ListScreenColumns<BranchListItem> = ({ state }) => {
    const cols: ReadonlyArray<DataGridColumn<BranchListItem>> = [
      {
        id: 'serial',
        header: 'S.No',
        width: '64px',
        cell: (_row, index) => (
          <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
        ),
      },
      {
        id: 'name',
        header: 'Name',
        sortable: true,
        cell: (row) => (
          <div className="flex flex-col">
            <span className="font-medium text-zinc-900">{row.name}</span>
            <span className="text-xs text-zinc-500">{row.code}</span>
          </div>
        ),
      },
      {
        id: 'city',
        header: 'City',
        cell: (row) => <span className="text-zinc-600">{row.city?.name ?? '-'}</span>,
      },
      {
        id: 'state',
        header: 'State',
        cell: (row) => <span className="text-zinc-600">{row.state?.name ?? '-'}</span>,
      },
      {
        id: 'country',
        header: 'Country',
        cell: (row) =>
          row.country ? (
            <span className="text-zinc-600">
              {row.country.name} ({row.country.isoCode.toUpperCase()})
            </span>
          ) : (
            <span className="text-zinc-400">-</span>
          ),
      },
      {
        id: 'phone',
        header: 'Phone',
        cell: (row) => <span className="text-zinc-600">{row.phone ?? '-'}</span>,
      },
      {
        id: 'status',
        header: 'Status',
        cell: (row) => <UzStatusBadge status={row.isActive ? 'active' : 'inactive'} />,
      },
    ];
    return cols;
  };

  return (
    <ListScreen<BranchListItem, BranchListQuery>
      title="Branches"
      description="Offices used for invoicing and as the home branch of users, customers, and leads."
      useList={useBranchesList}
      defaultSort={{ by: 'sortOrder', dir: 'asc' }}
      minSearchLength={MIN_SEARCH_LENGTH}
      toQuery={(state, base) => {
        const status = state.filters.status;
        const countryFilter = state.filters.country;
        return {
          ...base,
          ...(countryFilter ? { countryId: countryFilter } : {}),
          ...(status === 'active' ? { isActive: true } : {}),
          ...(status === 'inactive' ? { isActive: false } : {}),
        };
      }}
      columns={columns}
      filters={filters}
      getRowId={(row) => row.id}
      createButton={{ label: 'Add branch' }}
      renderDrawer={({ open, mode, row, close }) => (
        <BranchFormDrawer
          open={open}
          onOpenChange={(o) => {
            if (!o) close();
          }}
          mode={mode}
          branch={row}
        />
      )}
      bulkActions={{
        delete: {
          onDelete: async (rows) => {
            await bulkDeleteMut.mutateAsync(rows.map((r) => r.id));
          },
          confirm: (rows) => ({
            title: `Delete ${rows.length} branch${rows.length === 1 ? '' : 'es'}?`,
            description:
              'These branches will be soft-deleted and hidden from lists. Users and leads linked to them are unaffected.',
          }),
        },
        setActive: {
          onSetActive: async (rows, isActive) =>
            bulkSetStatusMut.mutateAsync({ ids: rows.map((r) => r.id), isActive }),
          confirm: (rows, isActive) => ({
            title: `${isActive ? 'Activate' : 'Deactivate'} ${rows.length} branch${rows.length === 1 ? '' : 'es'}?`,
            description: isActive
              ? 'They will become selectable again in user and lead forms.'
              : 'They will be hidden from new selections but remain on historical records.',
          }),
        },
      }}
      export={{
        fetchPage: (q) => listBranches(q),
        filename: 'branches',
        canExport: canExportFinance,
        csv: [
          { header: 'Code', value: (r) => r.code },
          { header: 'Name', value: (r) => r.name },
          { header: 'City', value: (r) => r.city?.name ?? '' },
          { header: 'State', value: (r) => r.state?.name ?? '' },
          { header: 'Country', value: (r) => r.country?.name ?? '' },
          { header: 'Phone', value: (r) => r.phone ?? '' },
          { header: 'Email', value: (r) => r.email ?? '' },
          { header: 'GSTIN', value: (r) => r.gstin ?? '' },
          { header: 'Office badge', value: (r) => r.officeBadge ?? '' },
          { header: 'Status', value: (r) => (r.isActive ? 'Active' : 'Inactive') },
          { header: 'Sort order', value: (r) => r.sortOrder },
          { header: 'Legacy ID', value: (r) => r.legacyId ?? '' },
          { header: 'Created at', value: (r) => r.createdAt },
        ],
      }}
      rowActions={{
        edit: true,
        delete: {
          onDelete: (row) => deleteBranch.mutateAsync(row.id),
          disabled: () => !canDelete,
          confirm: (row) => ({
            title: 'Delete this branch?',
            description: `${row.name} will be soft-deleted and hidden from lists. Users and leads linked to it are unaffected.`,
          }),
        },
      }}
      copy={{
        searchPlaceholder: 'Search by name, code, GSTIN, or email (min 3 characters)',
        emptyTitle: 'No branches match these filters',
        emptyDescription: 'Adjust the country / status filter or clear the search box.',
        errorTitle: 'Failed to load branches',
        errorDescription: 'The server returned an error. Try refreshing or check your network.',
      }}
    />
  );
};
